/**
 * PATH: packages/ui/src/kanban/hooks/useDismissOnOutsideInteraction.ts
 * FILE_ID: UI-KANBAN-HOOK-DISMISS-002
 * PURPOSE: Close portaled context menus on outside pointer, contextmenu, or Esc.
 * WHY: BUG-2026-05-21-KANBAN-BOARD-CTXMENU-STUCK — bubble-phase mousedown on
 *      `document` never fired when operators clicked the Gantt/timeline canvas
 *      (and other surfaces that stop propagation). Capture-phase + backdrop
 *      matches native OS menu behaviour.
 * FLOW:
 *   ┌─────────────┐    ┌────────────────────────┐    ┌─────────┐
 *   │ pointerdown │ →  │ outside menu root?     │ →  │ onClose │
 *   │ (capture)   │    │ (shouldDismiss helper) │    └─────────┘
 *   └─────────────┘    └────────────────────────┘
 * DEPENDENCIES:
 *   - shouldDismissContextMenu: pure outside-target check
 * RELATED:
 *   - KanbanBoardContextMenu.tsx: primary consumer (board rail menu)
 */

import { useEffect, type RefObject } from 'react'

import { shouldDismissContextMenu } from './shouldDismissContextMenu'

export function useDismissOnOutsideInteraction(
  menuRef: RefObject<HTMLElement | null>,
  onClose: () => void,
  active = true,
): void {
  useEffect(() => {
    if (!active) return

    const dismissIfOutside = (target: EventTarget | null) => {
      if (shouldDismissContextMenu(menuRef.current, target)) onClose()
    }

    const onPointerDown = (e: PointerEvent) => dismissIfOutside(e.target)
    const onContextMenu = (e: MouseEvent) => dismissIfOutside(e.target)
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }

    document.addEventListener('pointerdown', onPointerDown, true)
    document.addEventListener('contextmenu', onContextMenu, true)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true)
      document.removeEventListener('contextmenu', onContextMenu, true)
      document.removeEventListener('keydown', onKey)
    }
  }, [active, menuRef, onClose])
}
